import { supabase } from '@/integrations/supabase/client';
import { V3ProjectMember, V3ProjectMemberCreate, V3ProjectRole } from '@/integrations/supabase/v3-types';

export interface WorkspaceMemberForProject {
  user_id: string;
  user_email: string;
  user_display_name?: string;
  user_job_title?: string;
  workspace_role: string;
  is_already_assigned: boolean;
}

export class ProjectMemberService {
  /**
   * Get all members for a specific project
   */
  static async getProjectMembers(projectId: string): Promise<V3ProjectMember[]> {
    try {
      const { data, error } = await (supabase as any)
        .rpc('get_project_members', { project_uuid: projectId });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting project members:', error);
      return [];
    }
  }

  /**
   * Get available workspace members that can be assigned to a project
   */
  static async getAvailableWorkspaceMembers(projectId: string): Promise<WorkspaceMemberForProject[]> {
    try {
      const { data, error } = await (supabase as any)
        .rpc('get_available_workspace_members_for_project', { project_uuid: projectId });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting available workspace members:', error);
      return [];
    }
  }

  /**
   * Assign a user to a project with a specific role
   */
  static async assignMemberToProject(
    projectId: string, 
    userId: string, 
    role: V3ProjectRole
  ): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await (supabase as any)
        .from('project_members')
        .insert([{
          project_id: projectId,
          user_id: userId,
          role: role,
          assigned_by: user.id
        }]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error assigning member to project:', error);
      return false;
    }
  }

  /**
   * Update a project member's role
   */
  static async updateProjectMemberRole(
    projectMemberId: string, 
    newRole: V3ProjectRole
  ): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await (supabase as any)
        .from('project_members')
        .update({
          role: newRole,
          assigned_by: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectMemberId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating project member role:', error);
      return false;
    }
  }

  /**
   * Remove a member from a project
   */
  static async removeProjectMember(projectMemberId: string): Promise<boolean> {
    try {
      const { error } = await (supabase as any)
        .from('project_members')
        .delete()
        .eq('id', projectMemberId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error removing project member:', error);
      return false;
    }
  }

  /**
   * Check if current user is project admin
   */
  static async isProjectAdmin(projectId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await (supabase as any)
        .from('project_members')
        .select('role')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .single();

      if (error || !data) return false;
      return data.role === 'project_admin';
    } catch (error) {
      console.error('Error checking project admin status:', error);
      return false;
    }
  }

  /**
   * Check if current user is project lead
   */
  static async isProjectLead(projectId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await (supabase as any)
        .from('project_members')
        .select('role')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .single();

      if (error || !data) return false;
      return data.role === 'project_lead';
    } catch (error) {
      console.error('Error checking project lead status:', error);
      return false;
    }
  }

  /**
   * Get current user's project role
   */
  static async getCurrentUserProjectRole(projectId: string): Promise<V3ProjectRole | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await (supabase as any)
        .from('project_members')
        .select('role')
        .eq('project_id', projectId)
        .eq('user_id', user.id)
        .single();

      if (error || !data) return null;
      return data.role;
    } catch (error) {
      console.error('Error getting current user project role:', error);
      return null;
    }
  }

  /**
   * Get all projects that the current user has access to
   */
  static async getUserAccessibleProjects(): Promise<any[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await (supabase as any)
        .from('project_members')
        .select(`
          project_id,
          role,
          products:project_id (
            id,
            name,
            description,
            code,
            workspace_id,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', user.id)
        .order('products(name)');

      if (error) throw error;
      
      // Return the products with the user's role for each project
      return data?.map((item: any) => ({
        ...item.products,
        user_role: item.role
      })) || [];
    } catch (error) {
      console.error('Error getting user accessible projects:', error);
      return [];
    }
  }
} 